import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { visionsApi, goalsApi, activitiesApi, Vision, Goal } from '../../../src/api/client';
import { entityMutation } from '../../../src/api/queryClient';
import { encrypt, decrypt, encryptTitle, mergeEncryptionMetadata, decryptEntityTitle, getKeyForTeam } from '../../../src/utils/encryption';
import { GOAL_STATUS_COLORS } from '../../../src/theme';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import AiSuggestionsCard from '../../../src/components/AiSuggestionsCard';
import AiUnavailableBanner from '../../../src/components/AiUnavailableBanner';
import { useOnDeviceLlm } from '../../../src/hooks/useOnDeviceLlm';
import type { SuggestionItem, GoalCoverageResult } from '../../../src/services/llm';
import { Measure, initializeMilestones } from '../../../src/utils/milestones';
import { randomUUID } from '../../../src/utils/uuid';
import { isToday } from '../../../src/utils/format';
import ScreenHeader from '../../../src/components/ScreenHeader';

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  centered: { flex: 1, justifyContent: 'center' as const, alignItems: 'center' as const, backgroundColor: theme.colors.bgSecondary },
  content: { flex: 1 },
  contentInner: { padding: 16, paddingBottom: 40 },
  title: { fontSize: 22, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 16 },
  titleInput: { fontSize: 22, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 16, borderBottomWidth: 2, borderBottomColor: theme.colors.primary, paddingBottom: 8 },
  sectionLabel: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 8, marginTop: 16 },
  progressCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 20, alignItems: 'center' as const, ...theme.shadows.sm },
  progressPercent: { fontSize: 36, fontWeight: '800' as const, color: theme.colors.primary, marginBottom: 12 },
  progressTrack: { height: 8, backgroundColor: theme.colors.bgTertiary, borderRadius: 4, overflow: 'hidden' as const, width: '100%' as const, marginBottom: 8 },
  progressFill: { height: '100%' as const, backgroundColor: theme.colors.primary, borderRadius: 4 },
  progressDetail: { fontSize: 13, color: theme.colors.textMuted, fontWeight: '500' as const },
  infoCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, ...theme.shadows.sm },
  infoText: { fontSize: 15, color: theme.colors.textSecondary },
  descriptionText: { fontSize: 15, color: theme.colors.textSecondary, lineHeight: 22 },
  descriptionInput: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, fontSize: 15, color: theme.colors.textPrimary, minHeight: 120, borderWidth: 1, borderColor: theme.colors.border },
  aiButton: { backgroundColor: theme.colors.aiBg, borderWidth: 1, borderColor: theme.colors.aiBorder, borderRadius: 10, paddingVertical: 12, alignItems: 'center' as const, marginTop: 16 },
  aiButtonText: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.aiText },
  goalsSectionHeader: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, marginTop: 16, marginBottom: 8 },
  addGoalText: { fontSize: 14, color: theme.colors.primary, fontWeight: '600' as const },
  goalCard: { backgroundColor: theme.colors.background, borderRadius: 10, padding: 14, marginBottom: 6, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.03, shadowRadius: 3, elevation: 1 },
  goalCardContent: { flex: 1 },
  goalTopRow: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, marginBottom: 6 },
  goalTitle: { fontSize: 14, color: theme.colors.textPrimary, flex: 1, marginRight: 8 },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  statusBadgeText: { fontSize: 11, fontWeight: '700' as const, textTransform: 'uppercase' as const },
  goalProgressSection: { marginBottom: 4 },
  goalProgressTrack: { height: 4, backgroundColor: theme.colors.bgTertiary, borderRadius: 2, overflow: 'hidden' as const, marginBottom: 4 },
  goalProgressFill: { height: '100%' as const, backgroundColor: theme.colors.primary, borderRadius: 2 },
  goalProgressText: { fontSize: 11, color: theme.colors.textMuted, fontWeight: '500' as const },
  goalDueDate: { fontSize: 11, color: theme.colors.textDisabled, fontWeight: '500' as const },
  impactBadge: { marginTop: 12, backgroundColor: theme.colors.primaryBg, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  impactText: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.primary, textAlign: 'center' as const },
  futureLetterCard: { backgroundColor: theme.colors.primaryBg, borderRadius: 12, padding: 16, borderLeftWidth: 3, borderLeftColor: theme.colors.primary },
  futureLetterText: { fontSize: 15, color: theme.colors.textSecondary, lineHeight: 22, fontStyle: 'italic' as const },
  coverageCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, ...theme.shadows.sm },
  coverageDimRow: { flexDirection: 'row' as const, alignItems: 'center' as const, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
  coverageStatusDot: { width: 10, height: 10, borderRadius: 5, marginRight: 10 },
  coverageDimContent: { flex: 1, marginRight: 8 },
  coverageDimName: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textPrimary },
  coverageDimDetail: { fontSize: 12, color: theme.colors.textMuted, marginTop: 2 },
  coverageSummary: { fontSize: 13, color: theme.colors.textSecondary, marginTop: 12, lineHeight: 18, fontStyle: 'italic' as const },
});

export default function VisionDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const [vision, setVision] = useState<Vision | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [activityCounts, setActivityCounts] = useState<Record<string, { total: number; done: number }>>({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSaving, setSaving] = useState(false);
  const [editFutureLetter, setEditFutureLetter] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState<SuggestionItem[]>([]);
  const [showAiResults, setShowAiResults] = useState(false);
  const [impactScore, setImpactScore] = useState<{ connected: number; total: number } | null>(null);
  const [coverageResult, setCoverageResult] = useState<GoalCoverageResult | null>(null);
  const [showCoverage, setShowCoverage] = useState(false);
  const [creatingGapGoals, setCreatingGapGoals] = useState(false);
  const llm = useOnDeviceLlm();
  const [goalTitles, setGoalTitles] = useState<Record<string, string>>({});
  const [goalDates, setGoalDates] = useState<Record<string, { endDate?: string }>>({});

  useFocusEffect(
    useCallback(() => {
      if (!id || id === 'create') return;
      fetchVision();
    }, [id])
  );

  const fetchVision = async () => {
    setIsLoading(true);
    try {
      const [res, gRes, tRes] = await Promise.all([
        visionsApi.get(id!),
        goalsApi.list(),
        activitiesApi.list(),
      ]);
      setVision(res.data);
      const plainTitle = await decryptEntityTitle(res.data);
      setEditTitle(plainTitle);
      // Decrypt description
      const visionKey = await getKeyForTeam(res.data.team_id);
      if (res.data.description_encrypted && res.data.encryption_metadata?.iv) {
        try {
          const plainDesc = await decrypt(res.data.description_encrypted, { iv: res.data.encryption_metadata.iv }, visionKey);
          setEditDescription(plainDesc);
        } catch { setEditDescription(''); }
      } else {
        setEditDescription(res.data.description_encrypted || '');
      }
      // Decrypt future letter
      if (res.data.future_letter_encrypted && res.data.encryption_metadata?.future_letter_iv) {
        try {
          const plainLetter = await decrypt(res.data.future_letter_encrypted, { iv: res.data.encryption_metadata.future_letter_iv }, visionKey);
          setEditFutureLetter(plainLetter);
        } catch { setEditFutureLetter(''); }
      } else {
        setEditFutureLetter('');
      }

      const visionGoals = gRes.data.filter((g) => g.vision_id === id);
      setGoals(visionGoals);
      // Decrypt goal titles
      const titleEntries = await Promise.all(
        visionGoals.map(async (g) => [g.id, await decryptEntityTitle(g)] as const)
      );
      setGoalTitles(Object.fromEntries(titleEntries));
      // Decrypt goal end dates for display
      const dateEntries = await Promise.all(
        visionGoals.map(async (g): Promise<[string, { endDate?: string }]> => {
          if (g.end_date_encrypted && g.encryption_metadata?.end_date_iv) {
            try {
              const gKey = await getKeyForTeam(g.team_id);
              return [g.id, { endDate: await decrypt(g.end_date_encrypted, { iv: g.encryption_metadata.end_date_iv }, gKey) }];
            } catch {}
          }
          return [g.id, {}];
        })
      );
      setGoalDates(Object.fromEntries(dateEntries));

      const allActivities = tRes.data;

      // Calculate activity counts per goal
      const goalIds = new Set(visionGoals.map((g: Goal) => g.id));
      const counts: Record<string, { total: number; done: number }> = {};
      let totalActivities = 0;
      let doneActivities = 0;

      allActivities.forEach((t) => {
        if (t.goal_id && goalIds.has(t.goal_id)) {
          if (!counts[t.goal_id]) counts[t.goal_id] = { total: 0, done: 0 };
          counts[t.goal_id].total++;
          totalActivities++;
          if (t.status === 'done') {
            counts[t.goal_id].done++;
            doneActivities++;
          }
        }
      });
      setActivityCounts(counts);
      setOverallProgress(totalActivities > 0 ? Math.round((doneActivities / totalActivities) * 100) : 0);

      // Compute impact score: how many of today's activities connect to this vision
      const todayActivities = allActivities.filter((t) => isToday(t.start_time));
      const connectedToday = todayActivities.filter((t) => t.goal_id && goalIds.has(t.goal_id)).length;
      setImpactScore({ connected: connectedToday, total: todayActivities.length });
    } catch {
      showAlert('Error', 'Failed to load vision.', () => router.back());
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!requireConnectivity()) return;
    if (!vision || !editTitle.trim()) return;
    setSaving(true);
    try {
      const key = await getKeyForTeam(vision.team_id);
      const { title_encrypted, title_iv } = await encryptTitle(editTitle.trim(), key);
      let descEncrypted: string | undefined;
      let descIv: string | undefined;
      if (editDescription.trim()) {
        const descResult = await encrypt(editDescription.trim(), key);
        descEncrypted = descResult.encrypted;
        descIv = descResult.metadata.iv;
      }
      let futureLetterEncrypted: string | undefined;
      const additionalIvs: Record<string, string> = {};
      if (editFutureLetter.trim()) {
        const letterResult = await encrypt(editFutureLetter.trim(), key);
        futureLetterEncrypted = letterResult.encrypted;
        additionalIvs.future_letter_iv = letterResult.metadata.iv;
      }
      const res = await entityMutation(() =>
        visionsApi.update(vision.id, {
          title_encrypted,
          description_encrypted: descEncrypted,
          future_letter_encrypted: futureLetterEncrypted,
          encryption_metadata: mergeEncryptionMetadata(descIv, title_iv, additionalIvs),
        }),
      );
      setVision(res.data);
      setIsEditing(false);
    } catch {
      showAlert('Error', 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!requireConnectivity()) return;
    showConfirm(
      'Delete Vision',
      `Delete "${editTitle}"? ${goals.length} goals will be unlinked from this vision.`,
      async () => {
        try {
          await entityMutation(() => visionsApi.delete(vision!.id));
          router.back();
        } catch {
          showAlert('Error', 'Failed to delete vision.');
        }
      },
      { confirmText: 'Delete', destructive: true }
    );
  };

  const handleAnalyzeCoverage = async () => {
    if (!vision) return;
    setShowCoverage(true);
    setCoverageResult(null);
    const titles = goals.map((g) => goalTitles[g.id] || 'Untitled');
    const result = await llm.analyzeGoalCoverage(editTitle.trim(), titles);
    if (result) setCoverageResult(result);
  };

  const handleCreateGapGoals = async () => {
    if (!requireConnectivity()) return;
    if (!vision || !coverageResult) return;
    const gapDimensions = coverageResult.dimensions
      .filter((d) => d.status === 'gap')
      .map((d) => d.name);
    if (gapDimensions.length === 0) return;

    setCreatingGapGoals(true);
    try {
      const generatedGoals = await llm.generateGapGoals(editTitle.trim(), gapDimensions);
      if (!generatedGoals || generatedGoals.length === 0) {
        showAlert('Error', 'AI could not generate goals. Please try again.');
        return;
      }

      const key = await getKeyForTeam(vision.team_id);

      for (const goal of generatedGoals) {
        const { title_encrypted, title_iv } = await encryptTitle(goal.title, key);
        const descResult = await encrypt(goal.description, key);
        const startDateResult = await encrypt(goal.start_date, key);
        const endDateResult = await encrypt(goal.end_date, key);

        let metricsEncrypted: string | undefined;
        let metricsIv: string | undefined;
        const effectiveMilestoneCount = goal.measures.length > 0 ? goal.milestone_count : null;

        if (goal.measures.length > 0) {
          const measuresData: Measure[] = goal.measures.map((m) => ({
            id: randomUUID(),
            name: m.name,
            unit: m.unit,
            start_value: m.start_value,
            target_value: m.target_value,
            milestones: effectiveMilestoneCount
              ? initializeMilestones(effectiveMilestoneCount)
              : [],
          }));
          const metricsResult = await encrypt(JSON.stringify(measuresData), key);
          metricsEncrypted = metricsResult.encrypted;
          metricsIv = metricsResult.metadata.iv;
        }

        await entityMutation(() =>
          goalsApi.create({
            title_encrypted,
            description_encrypted: descResult.encrypted,
            encryption_metadata: mergeEncryptionMetadata(descResult.metadata.iv, title_iv, {
              start_date_iv: startDateResult.metadata.iv,
              end_date_iv: endDateResult.metadata.iv,
              ...(metricsIv ? { metrics_iv: metricsIv } : {}),
            }),
            vision_id: vision.id,
            start_date_encrypted: startDateResult.encrypted,
            end_date_encrypted: endDateResult.encrypted,
            metrics_encrypted: metricsEncrypted,
            milestone_count: effectiveMilestoneCount ?? undefined,
          }),
        );
      }

      // Refresh data and re-analyze coverage
      await fetchVision();
      setShowCoverage(false);
      setCoverageResult(null);
      showAlert('Goals Created', `${generatedGoals.length} goal${generatedGoals.length > 1 ? 's' : ''} created to cover the gaps.`);
    } catch {
      showAlert('Error', 'Failed to create goals. Please try again.');
    } finally {
      setCreatingGapGoals(false);
    }
  };

  const handleAiSuggest = async () => {
    if (!vision) return;
    setShowAiResults(true);
    setAiSuggestions([]);
    const result = await llm.suggest('vision', `${editTitle.trim()}: ${editDescription.trim()}`);
    if (result) {
      setAiSuggestions(result.suggestions);
    }
  };

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!vision) return null;

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="Vision Details"
        left={
          isEditing
            ? {
                label: 'Cancel editing',
                text: 'Cancel',
                onPress: () => setIsEditing(false),
                testID: 'vision-detail.back-button',
              }
            : {
                label: 'Go back',
                icon: 'arrow-back',
                onPress: () => router.back(),
                testID: 'vision-detail.back-button',
              }
        }
        right={
          isEditing
            ? {
                label: 'Save changes',
                text: 'Save',
                tone: 'primary',
                onPress: handleSave,
                disabled: isSaving,
                loading: isSaving,
                testID: 'vision-detail.save-button',
              }
            : [
                {
                  label: 'Edit vision',
                  icon: 'create-outline',
                  onPress: () => setIsEditing(true),
                  testID: 'vision-detail.edit-button',
                },
                {
                  label: 'Delete vision',
                  icon: 'trash-outline',
                  tone: 'danger',
                  accessibilityHint: 'Permanently deletes this vision',
                  onPress: handleDelete,
                  testID: 'vision-detail.delete-button',
                },
              ]
        }
      />
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>
        {/* Title */}
        {isEditing ? (
          <TextInput
            style={styles.titleInput}
            value={editTitle}
            onChangeText={setEditTitle}
            placeholder="Vision title"
            autoFocus
            accessibilityLabel="Vision title"
            testID="vision-detail.title-input"
          />
        ) : (
          <Text style={styles.title}>{'\u2728'} {editTitle}</Text>
        )}

        {/* Overall Progress + Impact Score */}
        <Text style={styles.sectionLabel}>OVERALL PROGRESS</Text>
        <View style={styles.progressCard}>
          <Text style={styles.progressPercent}>{overallProgress}%</Text>
          <View style={styles.progressTrack}>
            <View style={[styles.progressFill, { width: `${overallProgress}%` }]} />
          </View>
          <Text style={styles.progressDetail}>
            {goals.filter((g) => g.status === 'completed').length}/{goals.length} goals completed
          </Text>
          {impactScore && impactScore.total > 0 && (
            <View style={styles.impactBadge}>
              <Text style={styles.impactText}>
                Today: {impactScore.connected} of {impactScore.total} activities connected
              </Text>
            </View>
          )}
        </View>

        {/* Description */}
        <Text style={styles.sectionLabel}>DESCRIPTION</Text>
        {isEditing ? (
          <TextInput
            style={styles.descriptionInput}
            value={editDescription}
            onChangeText={setEditDescription}
            placeholder="Describe your vision..."
            multiline
            textAlignVertical="top"
            accessibilityLabel="Vision description"
            testID="vision-detail.description-input"
          />
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.descriptionText}>
              {editDescription || 'No description yet'}
            </Text>
          </View>
        )}

        {/* Future Letter */}
        <Text style={styles.sectionLabel}>LETTER FROM YOUR FUTURE SELF</Text>
        {isEditing ? (
          <TextInput
            style={styles.descriptionInput}
            value={editFutureLetter}
            onChangeText={setEditFutureLetter}
            placeholder="Write as if you've already achieved this vision. What does your life look like? How does it feel?"
            placeholderTextColor={colors.placeholder}
            multiline
            textAlignVertical="top"
            accessibilityLabel="Future letter"
            testID="vision-detail.future-letter-input"
          />
        ) : editFutureLetter ? (
          <View style={styles.futureLetterCard}>
            <Text style={styles.futureLetterText}>
              {editFutureLetter}
            </Text>
          </View>
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.infoText}>No future letter yet. Tap edit to write one.</Text>
          </View>
        )}

        {/* AI Suggestions */}
        {llm.available === false && (
          <AiUnavailableBanner reason={llm.unavailableReason} />
        )}
        {llm.available === true && (
          <TouchableOpacity
            style={styles.aiButton}
            onPress={handleAiSuggest}
            disabled={llm.loading}
            accessibilityLabel="Get AI suggestions"
            accessibilityRole="button"
            testID="vision-detail.ai-suggest-button"
          >
            <Text style={styles.aiButtonText}>{'\u{1F9E0}'} AI Suggest — Refine vision or suggest goals</Text>
          </TouchableOpacity>
        )}

        {showAiResults && (
          <AiSuggestionsCard
            loading={llm.loading}
            modelDownloading={llm.modelDownloading}
            suggestions={aiSuggestions}
            error={llm.error}
            onApplySuggestion={(text) => {
              setEditDescription(text);
              setIsEditing(true);
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
            onDismiss={() => {
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
          />
        )}

        {/* Goal Coverage — view mode only */}
        {!isEditing && (
        <>
        <Text style={styles.sectionLabel}>GOAL COVERAGE</Text>
        {llm.available === true ? (
          <>
            <TouchableOpacity
              style={styles.aiButton}
              onPress={handleAnalyzeCoverage}
              disabled={llm.loading}
              accessibilityLabel="Analyze goal coverage"
              accessibilityRole="button"
              testID="vision-detail.analyze-coverage-button"
            >
              <Text style={styles.aiButtonText}>{'\u{1F50D}'} {coverageResult ? 'Re-analyze Coverage' : 'Analyze Coverage'}</Text>
            </TouchableOpacity>
            {showCoverage && coverageResult ? (
              <View style={styles.coverageCard}>
                {coverageResult.dimensions.map((dim, i) => (
                  <View key={i} style={styles.coverageDimRow}>
                    <View style={[styles.coverageStatusDot, { backgroundColor: dim.status === 'covered' ? colors.success : colors.warning }]} />
                    <View style={styles.coverageDimContent}>
                      <Text style={styles.coverageDimName}>{dim.name}</Text>
                      <Text style={styles.coverageDimDetail}>
                        {dim.status === 'covered'
                          ? dim.goals.join(', ')
                          : 'No goals address this'}
                      </Text>
                    </View>
                    <View style={[styles.statusBadge, { backgroundColor: dim.status === 'covered' ? colors.success + '20' : colors.warning + '20' }]}>
                      <Text style={[styles.statusBadgeText, { color: dim.status === 'covered' ? colors.success : colors.warning }]}>
                        {dim.status === 'covered' ? 'COVERED' : 'GAP'}
                      </Text>
                    </View>
                  </View>
                ))}
                {coverageResult.summary ? (
                  <Text style={styles.coverageSummary}>{coverageResult.summary}</Text>
                ) : null}
                {coverageResult.dimensions.some((d) => d.status === 'gap') && (
                  <TouchableOpacity
                    style={[styles.aiButton, { marginTop: 12 }]}
                    onPress={handleCreateGapGoals}
                    disabled={creatingGapGoals || llm.loading}
                    accessibilityLabel="Create goals for coverage gaps"
                    accessibilityRole="button"
                    testID="vision-detail.create-gap-goals-button"
                  >
                    {creatingGapGoals ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <ActivityIndicator size="small" color={colors.aiText} />
                        <Text style={styles.aiButtonText}>Creating goals...</Text>
                      </View>
                    ) : (
                      <Text style={styles.aiButtonText}>{'\u{2728}'} Create Goals for Gaps</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            ) : showCoverage ? (
              <View style={styles.infoCard}>
                <ActivityIndicator size="small" color={colors.primary} />
                <Text style={[styles.infoText, { marginTop: 8, textAlign: 'center' as const }]}>Analyzing coverage...</Text>
              </View>
            ) : null}
          </>
        ) : null}
        </>
        )}

        {/* Goals */}
        <View style={styles.goalsSectionHeader}>
          <Text style={styles.sectionLabel}>GOALS ({goals.length})</Text>
          <TouchableOpacity
            onPress={() => router.push('/(tabs)/goals/create')}
            accessibilityLabel="Add a goal to this vision"
            accessibilityRole="button"
            testID="vision-detail.add-goal-button"
          >
            <Text style={styles.addGoalText}>+ Add Goal</Text>
          </TouchableOpacity>
        </View>

        {goals.length > 0 ? (
          goals.map((g) => {
            const statusColor = GOAL_STATUS_COLORS[g.status] || { bg: colors.bgTertiary, text: colors.textMuted };

            const counts = activityCounts[g.id];
            const goalProgress = counts && counts.total > 0 ? Math.round((counts.done / counts.total) * 100) : 0;

            return (
              <TouchableOpacity
                key={g.id}
                style={styles.goalCard}
                onPress={() => router.push(`/(tabs)/goals/${g.id}` as any)}
                accessibilityLabel={`Goal: ${goalTitles[g.id] || 'Untitled'}, status: ${g.status}`}
                accessibilityRole="button"
                testID={`vision-detail.goal-card-${g.id}`}
              >
                <View style={styles.goalCardContent} accessible={false}>
                  <View style={styles.goalTopRow} accessible={false}>
                    <Text style={styles.goalTitle} numberOfLines={1} accessible={false}>{'\u{1F3AF}'} {goalTitles[g.id] || 'Untitled'}</Text>
                    <View style={[styles.statusBadge, { backgroundColor: statusColor.bg }]} accessible={false}>
                      <Text style={[styles.statusBadgeText, { color: statusColor.text }]} accessible={false}>{g.status}</Text>
                    </View>
                  </View>
                  {counts && counts.total > 0 && (
                    <View style={styles.goalProgressSection} accessible={false}>
                      <View style={styles.goalProgressTrack} accessible={false}>
                        <View style={[styles.goalProgressFill, { width: `${goalProgress}%` }]} accessible={false} />
                      </View>
                      <Text style={styles.goalProgressText} accessible={false}>{goalProgress}% · {counts.done}/{counts.total} activities</Text>
                    </View>
                  )}
                  {goalDates[g.id]?.endDate && (
                    <Text style={styles.goalDueDate} accessible={false}>
                      Due: {new Date(goalDates[g.id].endDate!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Text>
                  )}
                </View>
              </TouchableOpacity>
            );
          })
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.infoText}>No goals linked to this vision yet</Text>
          </View>
        )}

      </ScrollView>
    </View>
  );
}
